/**
 * Profile Manager (Phase 5 — Step 5.1).
 *
 * Local multi-profile management with isolated data per profile.
 */

/** A user profile */
export interface Profile {
  /** UUID */
  id: string;
  /** Display name */
  name: string;
  /** Avatar color hex */
  avatarColor: string;
  /** Profile type */
  type: 'student' | 'instructor' | 'advisor' | 'parent';
  /** ISO datetime created */
  createdAt: string;
}

const PROFILES_KEY = 'absence-budget-profiles';
const ACTIVE_PROFILE_KEY = 'absence-budget-active-profile';

const AVATAR_COLORS = ['#4a90d9', '#e8734a', '#50b86c', '#f5a623', '#9b59b6', '#1abc9c', '#e74c3c', '#3498db'];

/** Get all profiles from localStorage */
export function getProfiles(): Profile[] {
  try {
    const raw = localStorage.getItem(PROFILES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Save profiles to localStorage */
function saveProfiles(profiles: Profile[]): void {
  localStorage.setItem(PROFILES_KEY, JSON.stringify(profiles));
}

/** Get the active profile ID */
export function getActiveProfileId(): string | null {
  return localStorage.getItem(ACTIVE_PROFILE_KEY);
}

/** Set the active profile ID */
export function setActiveProfileId(id: string | null): void {
  if (id) {
    localStorage.setItem(ACTIVE_PROFILE_KEY, id);
  } else {
    localStorage.removeItem(ACTIVE_PROFILE_KEY);
  }
}

/** Create a new profile */
export function createProfile(name: string, type: Profile['type'] = 'student'): Profile {
  const profiles = getProfiles();
  const colorIndex = profiles.length % AVATAR_COLORS.length;

  const profile: Profile = {
    id: `profile-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name,
    avatarColor: AVATAR_COLORS[colorIndex],
    type,
    createdAt: new Date().toISOString(),
  };

  profiles.push(profile);
  saveProfiles(profiles);
  return profile;
}

/** Delete a profile and its IndexedDB data */
export async function deleteProfile(id: string): Promise<void> {
  const profiles = getProfiles();
  saveProfiles(profiles.filter((p) => p.id !== id));

  // Delete the profile's IndexedDB database
  try {
    const dbName = `absence-budget-db-${id}`;
    indexedDB.deleteDatabase(dbName);
  } catch {
    // Ignore errors — database may not exist
  }

  // If this was the active profile, clear it
  if (getActiveProfileId() === id) {
    setActiveProfileId(null);
  }
}

/** Get the active profile */
export function getActiveProfile(): Profile | null {
  const id = getActiveProfileId();
  if (!id) return null;
  return getProfiles().find((p) => p.id === id) ?? null;
}
